import { useState, useCallback } from 'react';
import { apiRequest } from '@/utils/api-request';

export interface DatasetQueryPayload {
  question: string;
  include_sql?: boolean;
  model?: string;
}

export interface DatasetQuerySuccess {
  rows: Array<Record<string, string | null>>;
  columns: string[];
  query_execution_id: string;
  output_location?: string | null;
  sql?: string | null;
  candidates_generated?: number | null;
  candidates_succeeded?: number | null;
  strategy?: string | null;
}

export interface DatasetQueryErrorDetail {
  message: string;
  query_execution_id?: string;
  state?: string;
  error_type?: string;
  error_message?: string;
  reason?: string;
  sql?: string | null;
}

interface UseDatasetQueryArgs {
  orgId: string;
  collectionId: string;
  datasetId: string;
}

interface UseDatasetQueryResult {
  runQuery: (payload: DatasetQueryPayload) => Promise<void>;
  data: DatasetQuerySuccess | null;
  error: DatasetQueryErrorDetail | null;
  isLoading: boolean;
}

export const useDatasetQuery = ({
  orgId,
  collectionId,
  datasetId,
}: UseDatasetQueryArgs): UseDatasetQueryResult => {
  const [data, setData] = useState<DatasetQuerySuccess | null>(null);
  const [error, setError] = useState<DatasetQueryErrorDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const runQuery = useCallback(
    async (payload: DatasetQueryPayload) => {
      const trimmedQuestion = payload.question.trim();
      if (!trimmedQuestion) {
        return;
      }

      setIsLoading(true);
      setError(null);

      const requestBody = {
        question: trimmedQuestion,
        ...(payload.include_sql ? { include_sql: true } : {}),
        ...(payload.model ? { model: payload.model } : {}),
      };

      try {
        const response = await apiRequest<DatasetQuerySuccess>({
          url: `/collections/${orgId}/${collectionId}/datasets/${datasetId}/query`,
          method: 'POST',
          body: requestBody,
          target: 'platform',
        });

        if (response.detail) {
          // apiRequest flattens non-string error details into a generic
          // message but preserves the raw body on `data`; recover the
          // structured detail (message/state/sql) from there when present.
          const rawDetail =
            (response.data as { detail?: unknown } | undefined)?.detail ??
            response.detail;
          const errorDetail =
            typeof rawDetail === 'object' && rawDetail !== null
              ? (rawDetail as DatasetQueryErrorDetail)
              : { message: String(rawDetail) };
          setError(errorDetail);
          setData(null);
        } else if (response.data) {
          // Handle successful response
          setData(response.data);
          setError(null);
        } else {
          // Unexpected response format
          setError({ message: 'Unexpected response format' });
          setData(null);
        }
      } catch (fetchError) {
        setError({
          message:
            fetchError instanceof Error
              ? fetchError.message
              : 'Unexpected error running query',
        });
        setData(null);
      } finally {
        setIsLoading(false);
      }
    },
    [collectionId, datasetId, orgId],
  );

  return {
    runQuery,
    data,
    error,
    isLoading,
  };
};
